import {
  ConsumptionRateConfig,
  FuelType,
  FuelTypeConfig,
  Meter,
} from "../../types/salesDocuments.types";

export const createMeter = (id: number, fuelType: FuelType): Meter => ({
  // Factory function to auto-assign meter id
  id,
  zohoId: "",
  fuelType,
  name: `Meter ${String(id)}`,
  hasError: false,
});

// Contains additional information for fuel types - primarily emoji (very important).
// Keyed by FuelType so adding a new fuel type is a compile error until it has a config
const fuelTypeConfigs: Record<FuelType, FuelTypeConfig> = {
  electricity: { type: "electricity", label: "Electricity", emoji: "⚡" },
  gas: { type: "gas", label: "Gas", emoji: "🔥" },
};

export const fuelTypes: FuelTypeConfig[] = Object.values(fuelTypeConfigs);

export const getFuelTypeConfig = (fuelType: FuelType): FuelTypeConfig =>
  fuelTypeConfigs[fuelType];

export const consumptionRates: ConsumptionRateConfig[] = [
  {
    id: "day-rate",
    label: "Day Rate Consumption",
    props: "dayRate",
    setter: "setDayRate",
    hasRate: true,
  },
  {
    id: "night-rate",
    label: "Night Rate Consumption",
    props: "nightRate",
    setter: "setNightRate",
    hasRate: true,
  },
  {
    id: "evening-weekend-rate",
    label: "Evening Weekend Rate",
    props: "eveningWeekendRate",
    setter: "setEveningWeekendRate",
    hasRate: true,
  },
  {
    id: "evening-weekend-night-rate",
    label: "Evening Weekend Night Rate",
    props: "eveningWeekendNightRate",
    setter: "setEveningWeekendNightRate",
    hasRate: true,
  },
  {
    id: "total-consumption",
    label: "Total Consumption",
    props: "totalConsumption",
    setter: "setTotalConsumption",
    hasRate: false,
  },
];

export const MeterFieldKeys = {
  Name: "nameComponent",
  Id: "meterIdComponent",
  HH: "hhMeterComponent",
  Fit: "fitChargeComponent",
  Standing: "standingChargeComponent",
  Consumption: "consumptionComponent",
  SimpleConsumption: "simpleConsumptionComponent",
  Supplier: "supplierComponent",
  NonCommodity: "nonCommodityCostComponent",
  ExtraDetails: "extraDetail", // MPAN/MPRN etc that isn't important
} as const;

export const meterFieldConfig: Record<FuelType, string[]> = {
  electricity: [
    MeterFieldKeys.Name,
    MeterFieldKeys.Id,
    MeterFieldKeys.Standing,
    MeterFieldKeys.Consumption,
    MeterFieldKeys.Supplier,
    MeterFieldKeys.NonCommodity,
    MeterFieldKeys.Fit,
    MeterFieldKeys.HH,
    MeterFieldKeys.ExtraDetails,
  ],
  gas: [
    MeterFieldKeys.Name,
    MeterFieldKeys.Id,
    MeterFieldKeys.Standing,
    MeterFieldKeys.SimpleConsumption,
    MeterFieldKeys.Supplier,
    MeterFieldKeys.NonCommodity,
    MeterFieldKeys.ExtraDetails,
  ],
};

export const fitChargeMeasurements: string[] = ["p/kWh", "£/kWh"];

// Supplier information
interface Supplier {
  value: string;
  label: string;
  products: SupplierProduct[];
}

interface SupplierProduct {
  id: string;
  productName: string;
  prompt: string;
  productStatement: string;
  allowedFuels: FuelType[];
}

export const suppliers: Supplier[] = [
  {
    value: "corona",
    label: "Corona",
    products: [
      {
        id: "corona-fixed-power",
        productName: "Fixed Power",
        prompt: "",
        productStatement:
          "The offer provided is fixed for the contract duration. Any new industry charges/changes are not included in our offer and may be charged on a pass-through basis.  All pass-through charges will be charged at the prevailing market rate. As of 02.09.2025 New electricity charges Nuclear RAB Obligation and Operation included in our fixed rates. Subject to T&C’s.",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "corona-shift-fixed-power",
        productName: "Shift Fixed Power",
        prompt: "",
        productStatement:
          "The offer provided is fixed for the contract duration. Any new industry charges/changes are not included in our offer and may be charged on a pass-through basis.  All pass-through charges will be charged at the prevailing market rate. As of 02.09.2025 New electricity charges Nuclear RAB Obligation and Operation included in our fixed rates. Subject to T&C’s.",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "corona-fixed-gas",
        productName: "Fixed Gas",
        prompt: "",
        productStatement:
          "The offer provided is fixed for the contract duration. Any new industry charges/changes are not included in our offer and may be charged on a pass-through basis.  All pass-through charges will be charged at the prevailing market rate. Subject to T&C’s.",
        allowedFuels: ["gas"],
      },
      {
        id: "corona-standard-gas",
        productName: "Standard Gas",
        prompt: "",
        productStatement:
          "Price may be subject to pass-through if costs increase more than what has been included. In addition, any new industry charges/changes are not included in our offers and will be charged on a pass-through basis. All pass-through charges will be charged at the prevailing market rate.",
        allowedFuels: ["gas"],
      },
    ],
  },
  {
    value: "engie",
    label: "Engie",
    products: [
      {
        id: "engie-guard",
        productName: "Guard",
        prompt: "",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-simple",
        productName: "Simple",
        prompt: "",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-reflect",
        productName: "Reflect",
        prompt: "",
        productStatement: "DuoS & TNUoS Charges.",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-freedom",
        productName: "Freedom",
        prompt: "Copy all charges marked as passthrough on the final page of the contract",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-simple-flex-enterprise",
        productName: "Simple Flex Enterprise",
        prompt:
          "Copy all charges marked as passthrough on the final page of the contract (usually DUoS Charges)",
        productStatement: "The unit rates above exclude the cost of the commodity.",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-market-tracker",
        productName: "Marker Tracker",
        prompt: "Copy all charges marked as passthrough on the final page of the contract",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "engie-market-choice",
        productName: "Marker Choice",
        prompt: "Copy all charges marked as passthrough on the final page of the contract",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
    ],
  },
  {
    value: "npower",
    label: "Npower",
    products: [
      {
        id: "npower-fixed-certainty-electricity-supply",
        productName: "Fixed Certainty Electricity Supply",
        prompt:
          "Copy and paste the information on the Service Price Annex page, include the Charges Excluded & Other Charges",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "npower-fixed-commodity",
        productName: "Fixed Commodity",
        prompt:
          "Copy and paste the information on the Service Price Annex page, include the Charges Excluded & Other Charges",

        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "npower-fixed-choice",
        productName: "Fixed Choice",
        prompt:
          "Copy and paste the information on the Service Price Annex page, include the Charges Excluded & Other Charges",
        productStatement: "",
        allowedFuels: ["electricity", "gas"],
      },
      {
        id: "npower-multipurchase",
        productName: "Multipurchase",
        prompt:
          "Copy and paste the information on the Service Price Annex page, include the Charges Excluded & Other Charges",
        productStatement: "The unit rates above exclude the cost of the commodity.",
        allowedFuels: ["electricity", "gas"],
      },
    ],
  },
  {
    value: "other",
    label: "Other",
    products: [],
  },
];

export type EnergySupplier = (typeof suppliers)[number]["value"];
